import type {ReferralTrackingDetails} from 'src/types/referrer-portal';
import * as React from 'react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {getFormattedDate} from 'src/components/referral-v2/common';
import {
  type GenericHeaderItem,
  BasicSingleCell,
  BodyMedium,
  Truncate,
  Link,
} from '@spaced-out/ui-design-system/lib/components/index';
import ReferralDetailSidePanel from 'src/components/referral-v2/tracking/tracking-referral-detail';
import css from 'src/components/referral-v2/tracking/tracking.css';


export const getHeaders = (
  fileContainer: string,
  t: (arg1: string, arg2: string) => string,
  language?: string,
): GenericHeaderItem<
  ReferralTrackingDetails,
  Record<any, any> /*Extra argument type*/
>[] => {
  const headers = [
    {
      key: 'name',
      label: t('Referral_Name', 'Referral Name'),
      // @ts-expect-error - TS7031 - Binding element 'data' implicitly has an 'any' type.
      render: ({data}) => (
        <ReferralName referralDetails={data} fileContainer={fileContainer} />
      ),
      sortable: true,
    },
    {
      key: 'status',
      label: t('Status', 'Status'),
      sortable: true,
    },

    {
      key: 'job_title',
      label: t('Job', 'Job'),
      // @ts-expect-error - TS7031 - Binding element 'data' implicitly has an 'any' type.
      render: ({data}) => {
        return <JobTitle jobTitle={data.job_title} />;
      },
    },
    {
      key: 'agency_name',
      label: t('Organization', 'Organization'),
      // @ts-expect-error - TS7031 - Binding element 'data' implicitly has an 'any' type.
      render: ({data}) => (
        <BasicSingleCell className={css.tableCellWidth}>
          <BodyMedium>
            <Truncate
              line={1}
              showFullTextOnHover={true}
              className={css.truncate}
            >
              {data.agency_name ?? '-'}
            </Truncate>
          </BodyMedium>
        </BasicSingleCell>
      ),
    },
    {
      key: 'program_name',
      label: t('Program_Name', 'Program Name'),
      // @ts-expect-error - TS7031 - Binding element 'data' implicitly has an 'any' type.
      render: ({data}) => (
        <BasicSingleCell className={css.tableCellWidth}>
          <BodyMedium>
            <Truncate
              line={1}
              showFullTextOnHover={true}
              className={css.truncate}
            >
              {data.program_name ?? '-'}
            </Truncate>
          </BodyMedium>
        </BasicSingleCell>
      ),
    },
    {
      key: 'bonus_amount',
      label: t('Bonus', 'Bonus'),
      sortable: true,
      // @ts-expect-error - TS7031 - Binding element 'data' implicitly has an 'any' type.
      render: ({data}) => (
        <BasicSingleCell>
          <BodyMedium>
            {data.bonus_amount ? `$${data.bonus_amount}` : '-'}
          </BodyMedium>
        </BasicSingleCell>
      ),
    },
    {
      key: 'referred_on',
      label: t('Referred_On', 'Referred On'),
      sortable: true,
      // @ts-expect-error - TS7031 - Binding element 'data' implicitly has an 'any' type.
      render: ({data}) => (
        <BasicSingleCell className={css.tableCellWidth}>
          <BodyMedium>
            {getFormattedDate(data.referred_on, language)}
          </BodyMedium>
        </BasicSingleCell>
      ),
    },
    {
      key: 'updated_on',
      label: t('Last_Updated', 'Last Updated'),
      sortable: true,
      // @ts-expect-error - TS7031 - Binding element 'data' implicitly has an 'any' type.
      render: ({data}) => (
        <BasicSingleCell className={css.tableCellWidth}>
          <BodyMedium>{getFormattedDate(data.updated_on, language)}</BodyMedium>
        </BasicSingleCell>
      ),
    },
  ];
  // @ts-expect-error - TS2322 - Type '({ key: string; label: string; render: ({ data }: { data: any; }) => Element; sortable: boolean; } | { key: string; label: string; sortable: boolean; render?: undefined; } | { key: string; label: string; render: ({ data }: { ...; }) => Element; sortable?: undefined; })[]' is not assignable to type 'GenericHeaderItem<ReferralTrackingDetails, Record<any, any>>[]'.
  return headers;
};

const JobTitle = ({jobTitle}: {jobTitle: string}) => {
  const {t} = useLabelTranslation();
  return (
    <BasicSingleCell className={css.tableCellWidth}>
      {jobTitle ? (
        <BodyMedium>
          <Truncate
            className={css.truncate}
            line={1}
            showFullTextOnHover={true}
          >
            {jobTitle}
          </Truncate>
        </BodyMedium>
      ) : (
        <BodyMedium>{t('Unassigned', 'Unassigned')}</BodyMedium>
      )}
    </BasicSingleCell>
  );
};

const ReferralName = ({
  referralDetails,
  fileContainer,
}: {
  referralDetails: ReferralTrackingDetails;
  fileContainer: string;
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const {name} = referralDetails;
  return (
    <BasicSingleCell className={css.tableCellWidth}>
      <Link
        as="buttonTextMedium"
        className={css.pointer}
        onClick={() => setIsOpen(!isOpen)}
      >
        <Truncate line={1} showFullTextOnHover={true} className={css.truncate}>
          {name}
        </Truncate>
      </Link>
      {}
      <ReferralDetailSidePanel
        // @ts-expect-error - TS2739 - Type 'ReferralTrackingDetails' is missing the following properties from type 'ReferralTrackingDetails': archive, id
        referralTrackingDetails={referralDetails}
        onClose={() => setIsOpen(!isOpen)}
        isOpen={isOpen}
        fileContainer={fileContainer}
        showProgressBar={true}
      />
    </BasicSingleCell>
  );
};
